import type {
  ChartMapping,
  ChartType,
  ColumnDef,
  MappingFor,
  VisualizationConfig,
} from '@deckspace/types';

import * as Viz from '../visualizations/index.js';

export interface ChartRendererProps {
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
  config: VisualizationConfig;
  chartType: ChartType;
  mapping: ChartMapping;
  visualizationId?: string;
  themeKey?: string | number;
  width?: number;
  height?: number;
}

/**
 * Dispatcher: routes on `chartType` to the matching analytical chart component.
 * The `never` default branch makes adding a new ChartType a compile-time error here.
 *
 * Non-chart visualizations (Mermaid, Kanban, DataTable) are reached via the
 * surface model's own ComponentNode dispatch — they are NOT routed through here.
 */
export function ChartRenderer({
  rows,
  columnDefs,
  config,
  chartType,
  mapping,
  visualizationId: _visualizationId,
  themeKey,
  width,
  height,
}: ChartRendererProps) {
  switch (chartType) {
    case 'bar':
      return (
        <Viz.BarChart
          key={themeKey}
          rows={rows}
          columnDefs={columnDefs}
          mapping={mapping as MappingFor<'bar'>}
          config={config}
          width={width}
          height={height}
        />
      );
    case 'line':
      return (
        <Viz.LineChart
          key={themeKey}
          rows={rows}
          columnDefs={columnDefs}
          mapping={mapping as MappingFor<'line'>}
          config={config}
          width={width}
          height={height}
        />
      );
    case 'area':
      return (
        <Viz.AreaChart
          key={themeKey}
          rows={rows}
          columnDefs={columnDefs}
          mapping={mapping as MappingFor<'area'>}
          config={config}
          width={width}
          height={height}
        />
      );
    case 'pie':
      return (
        <Viz.PieChart
          key={themeKey}
          rows={rows}
          columnDefs={columnDefs}
          mapping={mapping as MappingFor<'pie'>}
          config={config}
          width={width}
          height={height}
        />
      );
    case 'scatter':
      return (
        <Viz.ScatterPlot
          key={themeKey}
          rows={rows}
          columnDefs={columnDefs}
          mapping={mapping as MappingFor<'scatter'>}
          config={config}
          width={width}
          height={height}
        />
      );
    case 'heatmap':
      return (
        <Viz.Heatmap
          key={themeKey}
          rows={rows}
          columnDefs={columnDefs}
          mapping={mapping as MappingFor<'heatmap'>}
          config={config}
          width={width}
          height={height}
        />
      );
    case 'histogram':
      return (
        <Viz.Histogram
          key={themeKey}
          rows={rows}
          columnDefs={columnDefs}
          mapping={mapping as MappingFor<'histogram'>}
          config={config}
          width={width}
          height={height}
        />
      );
    case 'radar':
      return (
        <Viz.RadarChart
          key={themeKey}
          rows={rows}
          columnDefs={columnDefs}
          mapping={mapping as MappingFor<'radar'>}
          config={config}
          width={width}
          height={height}
        />
      );
    case 'sankey':
      return (
        <Viz.SankeyChart
          key={themeKey}
          rows={rows}
          columnDefs={columnDefs}
          mapping={mapping as MappingFor<'sankey'>}
          config={config}
          width={width}
          height={height}
        />
      );
    case 'funnel':
      return (
        <Viz.FunnelChart
          key={themeKey}
          rows={rows}
          columnDefs={columnDefs}
          mapping={mapping as MappingFor<'funnel'>}
          config={config}
          width={width}
          height={height}
        />
      );
    case 'gauge':
      return (
        <Viz.GaugeChart
          key={themeKey}
          rows={rows}
          columnDefs={columnDefs}
          mapping={mapping as MappingFor<'gauge'>}
          config={config}
          width={width}
          height={height}
        />
      );
    default: {
      const _exhaustive: never = chartType;
      void _exhaustive;
      return (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <p className="text-sm">Unsupported chart type</p>
        </div>
      );
    }
  }
}
